import { useEffect, useState } from "react";
import { ScatterChart, Scatter, XAxis, YAxis, ZAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { CloudRain, Cloud, Sun, CloudSun } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Badge, Loading, CHART_GRID } from "@/components/kit";

const wIcon = (c) => (/rain/i.test(c) ? CloudRain : /cloud/i.test(c) ? Cloud : /clear|sun/i.test(c) ? Sun : CloudSun);
const dayLabel = (iso) => new Date(iso + "T00:00:00").toLocaleDateString("en-NZ", { weekday: "short" });

export default function WeatherIntel() {
  const { activeStore } = useAuth();
  const [d, setD] = useState(null);
  const [live, setLive] = useState(null);

  useEffect(() => {
    if (!activeStore) return;
    setD(null);
    setLive(null);
    api.get("/weather/intelligence", { params: { store_id: activeStore } }).then(({ data }) => setD(data));
    api.get("/weather", { params: { store_id: activeStore } }).then(({ data }) => setLive(data));
  }, [activeStore]);

  if (!d) return <Loading />;

  return (
    <div>
      <PageHeader
        title="Weather Intelligence"
        subtitle="How weather historically affects sales at this store"
        right={live && <Badge level="success">Live weather</Badge>}
      />

      {d.has_enough_history === false && (
        <Card className="p-4 mb-6 bg-amber-50/60 border-amber-200" testid="history-notice">
          <div className="text-sm text-amber-800">
            <span className="font-semibold">Not enough sales history yet</span> — only {d.history_days ?? 0} day{(d.history_days ?? 0) === 1 ? "" : "s"} of weather-matched data. Impact percentages need at least 3 observations per bucket, so some sections may be empty. Import more history (Settings → Sales data import) to fill these out.
          </div>
        </Card>
      )}

      {live && (
        <Card className="p-5 mb-6" testid="live-forecast">
          <div className="text-xs font-semibold uppercase tracking-[0.1em] text-zinc-400 mb-4">{live.forecast.length}-day forecast · {live.source === "openweather" ? "OpenWeather (live)" : "demo"}</div>
          <div className="grid grid-cols-3 md:grid-cols-7 gap-3">
            {live.forecast.map((w) => {
              const WI = wIcon(w.condition);
              return (
                <div key={w.day} className="text-center p-3 rounded-lg border border-zinc-100">
                  <div className="text-xs font-medium text-zinc-500">{dayLabel(w.day)}</div>
                  <WI className="h-6 w-6 mx-auto my-2 text-zinc-700" />
                  <div className="font-display font-semibold tnum">{Math.round(w.temp)}°</div>
                  <div className="text-xs text-sky-600 tnum mt-1">{Math.round(w.rain_prob * 100)}%</div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6" testid="temp-impact">
          <div className="text-xs font-semibold uppercase tracking-[0.1em] text-zinc-400 mb-4">Temperature impact on hot coffee</div>
          <div className="space-y-3">
            {d.temp_impact.map((r) => (
              <div key={r.range} className="flex items-center gap-4">
                <div className="w-24 text-sm text-zinc-600">{r.range}</div>
                <div className="flex-1 h-6 bg-zinc-100 rounded-md overflow-hidden relative">
                  <div className={`h-full ${r.pct >= 0 ? "bg-emerald-500" : "bg-orange-400"}`} style={{ width: `${Math.min(100, 50 + r.pct)}%` }} />
                </div>
                <div className={`w-14 text-right tnum text-sm font-medium ${r.pct >= 0 ? "text-emerald-600" : "text-orange-600"}`}>{r.pct >= 0 ? "+" : ""}{r.pct}%</div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-6" testid="rain-impact">
          <div className="text-xs font-semibold uppercase tracking-[0.1em] text-zinc-400 mb-4">Rain impact on hot drinks</div>
          <div className="space-y-4 mt-6">
            {d.rain_impact.map((r) => (
              <div key={r.label} className="flex items-center justify-between border-b border-zinc-100 pb-3">
                <span className="text-sm text-zinc-700">{r.label}</span>
                <Badge level={r.pct > 0 ? "success" : r.pct < 0 ? "warning" : "muted"}>{r.pct > 0 ? "+" : ""}{r.pct}% {r.pct === 0 ? "baseline" : ""}</Badge>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-6 lg:col-span-2" testid="temp-scatter">
          <div className="text-xs font-semibold uppercase tracking-[0.1em] text-zinc-400 mb-4">Temperature vs product mix (hot vs cold drinks)</div>
          <ResponsiveContainer width="100%" height={320}>
            <ScatterChart margin={{ left: 10, bottom: 10 }}>
              <CartesianGrid stroke={CHART_GRID} />
              <XAxis type="number" dataKey="temp" name="Temp" unit="°C" domain={["dataMin - 1", "dataMax + 1"]} />
              <YAxis type="number" name="Units" />
              <ZAxis range={[30, 30]} />
              <Tooltip cursor={{ strokeDasharray: "3 3" }} />
              <Scatter name="Hot drinks" data={d.scatter} dataKey="hot" fill="#ea580c" fillOpacity={0.5} />
              <Scatter name="Cold drinks" data={d.scatter} dataKey="cold" fill="#0ea5e9" fillOpacity={0.5} />
            </ScatterChart>
          </ResponsiveContainer>
          <div className="flex gap-6 justify-center mt-2 text-xs text-zinc-500">
            <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-orange-500" /> Hot drinks</span>
            <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-sky-500" /> Cold drinks</span>
          </div>
        </Card>

        <Card className="p-6 lg:col-span-2" testid="revenue-scatter">
          <div className="text-xs font-semibold uppercase tracking-[0.1em] text-zinc-400 mb-4">Temperature vs revenue</div>
          <ResponsiveContainer width="100%" height={280}>
            <ScatterChart margin={{ left: 10, bottom: 10 }}>
              <CartesianGrid stroke={CHART_GRID} />
              <XAxis type="number" dataKey="temp" name="Temp" unit="°C" />
              <YAxis type="number" dataKey="revenue" name="Revenue" unit="$" />
              <Tooltip cursor={{ strokeDasharray: "3 3" }} />
              <Scatter data={d.scatter} dataKey="revenue" fill="#18181b" fillOpacity={0.5} />
            </ScatterChart>
          </ResponsiveContainer>
        </Card>
      </div>
    </div>
  );
}
